import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { PlaidLinkOnExit, PlaidLinkOnSuccess, usePlaidLink } from "react-plaid-link";

import Button from "../common/Button";
import { API_URL } from "../../defs/urls";
import { useAuth } from "../../contexts/AuthContext";

interface PlaidLinkButtonProps {
    onLinked?: () => Promise<void> | void;
    className?: string;
}

interface LinkTokenResponse {
    success: boolean;
    data?: {
        link_token: string;
    };
    error?: string;
    message?: string;
}

interface ExchangeResponse {
    success: boolean;
    data?: unknown;
    error?: string;
    message?: string;
}

const ErrorMessage = styled.div`
    color: #d32f2f;
    margin-top: 8px;
    font-size: 0.875rem;
`;

const PlaidLinkButton = ({ onLinked, className }: PlaidLinkButtonProps): JSX.Element => {
    const { token } = useAuth();
    const [linkToken, setLinkToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [shouldOpen, setShouldOpen] = useState(false);

    const fetchLinkToken = useCallback(async () => {
        if (!token) {
            setError("You must be logged in to connect a bank.");
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/plaid/link-token`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data: LinkTokenResponse = await response.json();

            if (!response.ok || !data.success || !data.data?.link_token) {
                const reason = data.error || data.message || "Failed to create Plaid link token";
                throw new Error(reason);
            }

            setLinkToken(data.data.link_token);
            return data.data.link_token;
        } catch (fetchError) {
            console.error("Failed to create Plaid link token:", fetchError);
            const message = fetchError instanceof Error ? fetchError.message : "Failed to create Plaid link token";
            setError(message);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    const onSuccess = useCallback<PlaidLinkOnSuccess>(
        async (publicToken, metadata) => {
            if (!token) {
                setError("Session expired. Please log in again.");
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const response = await fetch(`${API_URL}/plaid/exchange-public-token`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        public_token: publicToken,
                        institution: metadata.institution
                            ? {
                                  id: metadata.institution.institution_id,
                                  name: metadata.institution.name,
                              }
                            : undefined,
                    }),
                });

                const data: ExchangeResponse = await response.json();

                if (!response.ok || !data.success) {
                    const reason = data.error || data.message || "Failed to exchange Plaid public token";
                    throw new Error(reason);
                }

                if (onLinked) {
                    await onLinked();
                }
            } catch (exchangeError) {
                console.error("Failed to exchange Plaid public token:", exchangeError);
                const message =
                    exchangeError instanceof Error ? exchangeError.message : "Failed to exchange Plaid public token";
                setError(message);
            } finally {
                setIsLoading(false);
            }
        },
        [token, onLinked],
    );

    const onExit = useCallback<PlaidLinkOnExit>((_error, metadata) => {
        if (_error) {
            console.warn("Plaid Link exited with error", _error, metadata);
        }
    }, []);

    const plaidConfig = useMemo(
        () => ({
            token: linkToken,
            onSuccess,
            onExit,
        }),
        [linkToken, onSuccess, onExit],
    );

    const { open, ready } = usePlaidLink(plaidConfig);

    useEffect(() => {
        if (shouldOpen && ready && open) {
            open();
            setShouldOpen(false);
        }
    }, [shouldOpen, ready, open]);

    const handleClick = async () => {
        if (isLoading) {
            return;
        }

        if (!linkToken) {
            const newToken = await fetchLinkToken();
            if (newToken) {
                setShouldOpen(true);
            }
            return;
        }

        if (!ready) {
            setShouldOpen(true);
            return;
        }

        open();
    };

    return (
        <div className={className}>
            <Button onClick={handleClick} disabled={isLoading || (!linkToken && !token)}>
                {isLoading ? "Connecting..." : "Connect bank with Plaid"}
            </Button>
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </div>
    );
};

export default PlaidLinkButton;
